import { ARI_CLIENT_PROFILES, ARI_INBOX_THREADS } from "@/mockData/ariInbox";
import type { AriClientBizData } from "@/types/ariPortfolio.types";
import type { AriInsightAlert } from "@/types/inbox.types";
import type { AriRosterClient } from "@/types/ariPortfolio.types";

export const ARI_CLIENT_BIZ_DATA: Record<string, AriClientBizData> = {
  "jay-z": {
    category: "Music",
    categoryColor: "#8b5cf6",
    commission: 0.1,
    company: {
      name: "Roc Nation LLC",
      type: "LLC",
      state: "Delaware",
      formed: "2008",
      ein: "47-XXXXXX1",
      address: "1411 Broadway, New York, NY 10018",
      officers: "Shawn Carter (CEO), Desiree Perez (COO)",
    },
    banking: {
      bank: "JP Morgan Private Bank",
      type: "Business Checking",
      routing: "021000021",
      account: "••••4729",
      wire: "CHASUS33",
      status: "Verified",
    },
    tax: {
      w9Status: "On File",
      filingStatus: "C-Corp",
      taxId: "47-XXXXXX1",
      lastFiled: "Mar 2025",
      nextDue: "Mar 2026",
    },
    teamMembers: [
      { name: "Desiree Perez", role: "COO, Roc Nation", side: "client" },
      { name: "Jay Brown", role: "Manager", side: "client" },
      { name: "Alex Rivera", role: "Lead Agent", side: "agency" },
      { name: "Maya Chen", role: "Legal Counsel", side: "agency" },
      { name: "Chris Park", role: "Finance Lead", side: "agency" },
    ],
    commissionHistory: [
      { year: "2021", dealVal: 220, commission: 22 },
      { year: "2022", dealVal: 180, commission: 18 },
      { year: "2023", dealVal: 195, commission: 19.5 },
      { year: "2024", dealVal: 240, commission: 24 },
      { year: "2025", dealVal: 300, commission: 30 },
      { year: "2026", dealVal: 90, commission: 9 },
    ],
    outlook12m: [
      { month: "May 2026", type: "deal", event: "Roc Nation expansion deal — final negotiation" },
      { month: "Jul 2026", type: "milestone", event: "Tidal earn-out triggers Q3" },
      { month: "Sep 2026", type: "financial", event: "Catalog publishing royalty payout" },
      { month: "Dec 2026", type: "compliance", event: "Year-end LLC tax filing prep" },
    ],
  },
  rihanna: {
    category: "Music",
    categoryColor: "#8b5cf6",
    commission: 0.1,
    company: {
      name: "Fenty Corp",
      type: "Corporation",
      state: "Delaware",
      formed: "2017",
      ein: "82-XXXXXX2",
      address: "345 N Maple Dr, Beverly Hills, CA 90210",
      officers: "Robyn Rihanna Fenty (Founder)",
    },
    banking: {
      bank: "Goldman Sachs Private Wealth",
      type: "Business Checking",
      routing: "124003116",
      account: "••••1182",
      wire: "GSCMUS33",
      status: "Verified",
    },
    tax: {
      w9Status: "On File",
      filingStatus: "S-Corp",
      taxId: "82-XXXXXX2",
      lastFiled: "Apr 2025",
      nextDue: "Apr 2026",
    },
    teamMembers: [
      { name: "Jay Brown", role: "Manager", side: "client" },
      { name: "Tina Fenty", role: "Family Advisor", side: "client" },
      { name: "Sarah Lee", role: "Lead Agent", side: "agency" },
      { name: "Maya Chen", role: "Legal Counsel", side: "agency" },
    ],
    commissionHistory: [
      { year: "2021", dealVal: 140, commission: 14 },
      { year: "2022", dealVal: 180, commission: 18 },
      { year: "2023", dealVal: 210, commission: 21 },
      { year: "2024", dealVal: 195, commission: 19.5 },
      { year: "2025", dealVal: 230, commission: 23 },
      { year: "2026", dealVal: 60, commission: 6 },
    ],
    outlook12m: [
      { month: "Jun 2026", type: "deal", event: "Nike comeback partnership signing" },
      { month: "Aug 2026", type: "marketing", event: "Fenty Beauty Asia launch" },
      { month: "Nov 2026", type: "milestone", event: "Album release — 5yr anniversary" },
    ],
  },
  lebron: {
    category: "Sports",
    categoryColor: "#22c55e",
    commission: 0.08,
    company: {
      name: "LRMR Ventures LLC",
      type: "LLC",
      state: "Ohio",
      formed: "2006",
      ein: "20-XXXXXX3",
      address: "1100 Town & Country Cmns, Akron, OH 44333",
      officers: "LeBron James (Founder), Maverick Carter (CEO)",
    },
    banking: {
      bank: "Wells Fargo Private Bank",
      type: "Business Checking",
      routing: "121000248",
      account: "••••6644",
      wire: "WFBIUS6S",
      status: "Verified",
    },
    tax: {
      w9Status: "On File",
      filingStatus: "LLC Pass-through",
      taxId: "20-XXXXXX3",
      lastFiled: "Apr 2025",
      nextDue: "Apr 2026",
    },
    teamMembers: [
      { name: "Maverick Carter", role: "CEO, SpringHill", side: "client" },
      { name: "Rich Paul", role: "Agent (Klutch)", side: "client" },
      { name: "Alex Rivera", role: "Lead Agent", side: "agency" },
      { name: "Jordan Kim", role: "Sports Counsel", side: "agency" },
      { name: "Chris Park", role: "Finance Lead", side: "agency" },
    ],
    commissionHistory: [
      { year: "2021", dealVal: 140, commission: 11.2 },
      { year: "2022", dealVal: 170, commission: 13.6 },
      { year: "2023", dealVal: 210, commission: 16.8 },
      { year: "2024", dealVal: 260, commission: 20.8 },
      { year: "2025", dealVal: 310, commission: 24.8 },
      { year: "2026", dealVal: 100, commission: 8 },
    ],
    outlook12m: [
      { month: "May 2026", type: "deal", event: "Nike royalty escalation triggered" },
      { month: "Jul 2026", type: "milestone", event: "SpringHill content slate renewal" },
      { month: "Oct 2026", type: "financial", event: "Klutch equity conversion review" },
    ],
  },
  kobe: {
    category: "Sports",
    categoryColor: "#22c55e",
    commission: 0.1,
    company: {
      name: "Kobe Inc.",
      type: "Corporation",
      state: "California",
      formed: "2013",
      ein: "46-XXXXXX4",
      address: "100 Bayview Cir, Newport Beach, CA 92660",
      officers: "Vanessa Bryant (Trustee)",
    },
    banking: {
      bank: "City National Bank",
      type: "Trust Account",
      routing: "122016066",
      account: "••••2408",
      wire: "CINAUS6L",
      status: "Verified",
    },
    tax: {
      w9Status: "On File",
      filingStatus: "Trust",
      taxId: "46-XXXXXX4",
      lastFiled: "Mar 2025",
      nextDue: "Mar 2026",
    },
    teamMembers: [
      { name: "Vanessa Bryant", role: "Trustee", side: "client" },
      { name: "Molly Carter", role: "Estate Counsel", side: "client" },
      { name: "Sarah Lee", role: "Lead Agent", side: "agency" },
      { name: "Jordan Kim", role: "Estate Liaison", side: "agency" },
    ],
    commissionHistory: [
      { year: "2021", dealVal: 31, commission: 3.1 },
      { year: "2022", dealVal: 28, commission: 2.8 },
      { year: "2023", dealVal: 35, commission: 3.5 },
      { year: "2024", dealVal: 40, commission: 4 },
      { year: "2025", dealVal: 42, commission: 4.2 },
      { year: "2026", dealVal: 12, commission: 1.2 },
    ],
    outlook12m: [
      { month: "Jun 2026", type: "deal", event: "Mamba/Nike licensing framework launch" },
      { month: "Aug 2026", type: "marketing", event: "Mambacita Foundation gala" },
      { month: "Dec 2026", type: "compliance", event: "Trust annual review filing" },
    ],
  },
  beyonce: {
    category: "Music",
    categoryColor: "#8b5cf6",
    commission: 0.1,
    company: {
      name: "Parkwood Entertainment LLC",
      type: "LLC",
      state: "New York",
      formed: "2010",
      ein: "27-XXXXXX5",
      address: "1411 Broadway, New York, NY 10018",
      officers: "Beyoncé Knowles-Carter (Founder)",
    },
    banking: {
      bank: "JP Morgan Private Bank",
      type: "Business Checking",
      routing: "021000021",
      account: "••••8821",
      wire: "CHASUS33",
      status: "Verified",
    },
    tax: {
      w9Status: "On File",
      filingStatus: "LLC Pass-through",
      taxId: "27-XXXXXX5",
      lastFiled: "Apr 2025",
      nextDue: "Apr 2026",
    },
    teamMembers: [
      { name: "Mathew Knowles", role: "Advisor", side: "client" },
      { name: "Yvette Noel-Schure", role: "Publicist", side: "client" },
      { name: "Sarah Lee", role: "Lead Agent", side: "agency" },
      { name: "Maya Chen", role: "Legal Counsel", side: "agency" },
    ],
    commissionHistory: [
      { year: "2021", dealVal: 180, commission: 18 },
      { year: "2022", dealVal: 200, commission: 20 },
      { year: "2023", dealVal: 580, commission: 58 },
      { year: "2024", dealVal: 160, commission: 16 },
      { year: "2025", dealVal: 190, commission: 19 },
      { year: "2026", dealVal: 80, commission: 8 },
    ],
    outlook12m: [
      { month: "May 2026", type: "deal", event: "Live Nation tour amendment signing" },
      { month: "Jul 2026", type: "milestone", event: "Renaissance II tour kickoff" },
      { month: "Oct 2026", type: "marketing", event: "Netflix concert film release" },
    ],
  },
  travis: {
    category: "Music",
    categoryColor: "#8b5cf6",
    commission: 0.1,
    company: {
      name: "Cactus Jack Records LLC",
      type: "LLC",
      state: "Texas",
      formed: "2017",
      ein: "82-XXXXXX6",
      address: "1213 Smith St, Houston, TX 77002",
      officers: "Jacques Webster II (Founder)",
    },
    banking: {
      bank: "Truist",
      type: "Business Checking",
      routing: "061000104",
      account: "••••3370",
      wire: "BRBTUS33",
      status: "Verified",
    },
    tax: {
      w9Status: "On File",
      filingStatus: "LLC Pass-through",
      taxId: "82-XXXXXX6",
      lastFiled: "Apr 2025",
      nextDue: "Apr 2026",
    },
    teamMembers: [
      { name: "David Stromberg", role: "Manager", side: "client" },
      { name: "Alex Rivera", role: "Lead Agent", side: "agency" },
      { name: "Maya Chen", role: "Legal Counsel", side: "agency" },
    ],
    commissionHistory: [
      { year: "2021", dealVal: 60, commission: 6 },
      { year: "2022", dealVal: 75, commission: 7.5 },
      { year: "2023", dealVal: 90, commission: 9 },
      { year: "2024", dealVal: 110, commission: 11 },
      { year: "2025", dealVal: 125, commission: 12.5 },
      { year: "2026", dealVal: 30, commission: 3 },
    ],
    outlook12m: [
      { month: "Jun 2026", type: "deal", event: "McDonald's Phase 2 global rollout" },
      { month: "Sep 2026", type: "marketing", event: "Cactus Jack apparel drop" },
      { month: "Dec 2026", type: "milestone", event: "World tour announcement" },
    ],
  },
  serena: {
    category: "Sports",
    categoryColor: "#22c55e",
    commission: 0.08,
    company: {
      name: "Serena Ventures LLC",
      type: "LLC",
      state: "California",
      formed: "2014",
      ein: "47-XXXXXX7",
      address: "1 Market St, San Francisco, CA 94105",
      officers: "Serena Williams (Founder & MP)",
    },
    banking: {
      bank: "Silicon Valley Bank (First Citizens)",
      type: "Business Checking",
      routing: "121140399",
      account: "••••9015",
      wire: "SVBKUS6S",
      status: "Verified",
    },
    tax: {
      w9Status: "On File",
      filingStatus: "LLC Pass-through",
      taxId: "47-XXXXXX7",
      lastFiled: "Apr 2025",
      nextDue: "Apr 2026",
    },
    teamMembers: [
      { name: "Alison Stillman", role: "GP, Serena Ventures", side: "client" },
      { name: "Jill Smoller", role: "Agent (WME)", side: "client" },
      { name: "Sarah Lee", role: "Lead Agent", side: "agency" },
      { name: "Jordan Kim", role: "Sports Counsel", side: "agency" },
    ],
    commissionHistory: [
      { year: "2021", dealVal: 45, commission: 3.6 },
      { year: "2022", dealVal: 40, commission: 3.2 },
      { year: "2023", dealVal: 38, commission: 3.04 },
      { year: "2024", dealVal: 42, commission: 3.36 },
      { year: "2025", dealVal: 46, commission: 3.68 },
      { year: "2026", dealVal: 15, commission: 1.2 },
    ],
    outlook12m: [
      { month: "May 2026", type: "deal", event: "Wilson legacy endorsement signing" },
      { month: "Aug 2026", type: "milestone", event: "Serena Ventures Fund III close" },
      { month: "Nov 2026", type: "marketing", event: "TIME 100 honoree feature" },
    ],
  },
};

const ROSTER_AVATARS: Record<string, string> = {
  "jay-z":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Jay-Z_%40_Shawn_%27Jay-Z%27_Carter_Foundation_Carnival_%28crop_2%29.jpg/960px-Jay-Z_%40_Shawn_%27Jay-Z%27_Carter_Foundation_Carnival_%28crop_2%29.jpg",
  rihanna:
    "https://upload.wikimedia.org/wikipedia/commons/2/25/Rihanna_Met_Gala_2014_%28cropped%29.jpg",
  lebron:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/LeBron_James_%2851914946203%29_%28cropped%29.jpg/960px-LeBron_James_%2851914946203%29_%28cropped%29.jpg",
  kobe:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Kobe_Bryant_2015.jpg/960px-Kobe_Bryant_2015.jpg",
  beyonce:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/Beyonce_Knowles_with_necklaces_%28cropped_2x3%29.jpg/960px-Beyonce_Knowles_with_necklaces_%28cropped_2x3%29.jpg",
  travis:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Travis_Scott_The_DAMN._Tour_%28cropped%29.jpg/960px-Travis_Scott_The_DAMN._Tour_%28cropped%29.jpg",
  serena:
    "https://upload.wikimedia.org/wikipedia/commons/d/d3/Serena_Williams_at_the_Australian_Open_2015_%28headshot%29.jpg",
};

function sumCommission(history: AriClientBizData["commissionHistory"]) {
  return history.reduce((sum, row) => sum + row.commission, 0);
}

function sumEarnings(history: AriClientBizData["commissionHistory"]) {
  return history.reduce((sum, row) => sum + row.dealVal, 0);
}

function countActiveDeals(
  pastDeals: AriRosterClient["pastDeals"],
) {
  return pastDeals.filter((deal) => deal.status === "Signed" || deal.status === "Active").length;
}

export function buildAriRosterClients(): AriRosterClient[] {
  return Object.values(ARI_CLIENT_PROFILES).map((profile) => {
    const biz = ARI_CLIENT_BIZ_DATA[profile.id];
    if (!biz) {
      throw new Error(`Missing biz data for client ${profile.id}`);
    }

    const totalCommission = sumCommission(biz.commissionHistory);
    const totalEarnings = sumEarnings(biz.commissionHistory);
    const activeDeals = countActiveDeals(profile.pastDeals);

    return {
      id: profile.id,
      name: profile.name,
      displayName: profile.name.split("(")[0]?.trim() || profile.name,
      title: profile.title,
      initials: profile.initials,
      avatarUrl: profile.avatarUrl ?? ROSTER_AVATARS[profile.id] ?? "",
      category: biz.category,
      categoryColor: biz.categoryColor,
      companyName: biz.company.name,
      bio: profile.bio,
      relationshipScore: profile.relationshipScore,
      outreachCount: profile.outreachCount,
      outreachNote: profile.outreachNote,
      social: profile.social,
      pastDeals: profile.pastDeals,
      totalEarnings,
      totalCommission,
      activeDeals,
      commissionRate: biz.commission,
      teamMembers: biz.teamMembers,
      biz,
    };
  });
}

export function getAriAlertsForClient(clientId: string): AriInsightAlert[] {
  const thread = ARI_INBOX_THREADS.find((item) => item.clientTags.includes(clientId));
  return thread?.ariAlerts ?? [];
}

export function getAriRosterTotals(clients: AriRosterClient[]) {
  return clients.reduce(
    (acc, client) => ({
      totalCommission: acc.totalCommission + client.totalCommission,
      totalEarnings: acc.totalEarnings + client.totalEarnings,
    }),
    { totalCommission: 0, totalEarnings: 0 },
  );
}

/** Maps Ari roster clients to Def Jam demo portfolio entities for FinancialsTab. */
export const ARI_CLIENT_ENTITY_MAP: Record<string, string> = {
  "jay-z": "dj-pe-2",
  rihanna: "dj-pe-1",
  lebron: "dj-pe-4",
  kobe: "dj-pe-3",
  beyonce: "dj-pe-1",
  travis: "dj-pe-2",
  serena: "dj-pe-4",
};

export function getAriClientPortfolioEntityId(clientId: string): string | null {
  return ARI_CLIENT_ENTITY_MAP[clientId] ?? null;
}

export function getDefaultClientNotes(clientId: string): string {
  const alerts = getAriAlertsForClient(clientId);
  if (alerts.length === 0) {
    return "";
  }

  return alerts.map((alert) => `• ${alert.text}`).join("\n\n");
}
